import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router';
import DashboardWrapper from '../../Shared/DashboardWrapper';
import Pagination from '../../Shared/Pagination';
import { SkeletonText } from '../../Shared/Skeletons';
import BackLink from '../MainDashboard/Common/BackLink';
import { formatDate } from '../MainDashboard/Common/formatters';
import { getExternalLink } from '../../../lib/api/external-link';
import { getEnquiryFunctionalityFeatureFlag } from '../../../lib/api/enquiry-functionality';
import { useEnquiries } from '../../../lib/hooks/useEnquiries';
import EnquiryStatusPill from './EnquiryStatusPill';
import { SORT_OPTIONS, STATUS_COPY, getColumnValue, normaliseDateRange } from './enquiryDisplay';

const PAGE_SIZE = 10;

export default function Enquiries() {
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState(null);
    const [sort, setSort] = useState('newest');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const dateRange = normaliseDateRange(dateFrom, dateTo);
    const { data, loading, error, reload } = useEnquiries({
        page,
        pageSize: PAGE_SIZE,
        status: statusFilter,
        sort: sort === 'newest' ? undefined : sort,
        from: dateRange.from,
        to: dateRange.to,
    });
    const [helpLink, setHelpLink] = useState('');
    const history = useHistory();

    useEffect(() => {
        getEnquiryFunctionalityFeatureFlag()
            .then((resp) => {
                if (resp?.data?.data !== true) history.push('/dashboard/main');
            })
            .catch(() => {
                history.push('/dashboard/main');
            });
    }, [history]);

    useEffect(() => {
        getExternalLink()
            .then((resp) => {
                setHelpLink(resp.data.faqLink);
            })
            .catch(() => {
                setHelpLink('');
            });
    }, []);

    const enquiries = Array.isArray(data?.data?.enquiries) ? data.data.enquiries : [];
    const totalMatches = data?.data?.totalMatches || 0;
    const statusCounts = data?.data?.statusCounts || null;

    const loadFailed = Boolean(error) || data?.isSuccess === false;
    const maxPage = Math.max(1, Math.ceil(totalMatches / PAGE_SIZE));

    useEffect(() => {
        if (page > maxPage) setPage(1);
    }, [maxPage, page]);

    const statusTabs = [
        { key: null, label: statusCounts ? `All (${statusCounts.all})` : 'All', id: 'enquiries-tab-all' },
        { key: 'Received', label: STATUS_COPY.Received.label, id: 'enquiries-tab-received' },
        { key: 'InProgress', label: STATUS_COPY.InProgress.label, id: 'enquiries-tab-in-progress' },
        { key: 'Complete', label: STATUS_COPY.Complete.label, id: 'enquiries-tab-complete' },
    ];

    const selectStatusTab = (key) => {
        setStatusFilter(key);
        setPage(1);
    };

    const selectSort = (value) => {
        setSort(value);
        setPage(1);
    };

    const changeDateFrom = (value) => {
        setDateFrom(value);
        setPage(1);
    };

    const changeDateTo = (value) => {
        setDateTo(value);
        setPage(1);
    };

    const clearDates = () => {
        setDateFrom('');
        setDateTo('');
        setPage(1);
    };

    const dateFilterActive = Boolean(dateRange.from || dateRange.to);
    const filtersActive = dateFilterActive || Boolean(statusFilter);

    return (
        <DashboardWrapper helpLink={helpLink}>
            <div className="main-dashboard" id="enquiries-page">
                <BackLink />

                <section className="enquiries__heading" id="enquiries-heading">
                    <h1>Your enquiries</h1>
                    <button
                        type="button"
                        className="enquiries__raise-btn"
                        id="raise-enquiry-button"
                        onClick={() => history.push('/enquiries/new')}
                    >
                        Raise an enquiry
                    </button>
                </section>

                {!loadFailed && (
                    <div className="enquiries__tabs" id="enquiries-tabs">
                        {statusTabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                id={tab.id}
                                className={`enquiry-tab${statusFilter === tab.key ? ' enquiry-tab--active' : ''}`}
                                onClick={() => selectStatusTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                )}

                {!loadFailed && (
                    <div className="enquiries__filters" id="enquiries-filters">
                        <label className="enquiries__filter-label" htmlFor="enquiries-sort">
                            Sort
                        </label>
                        <select
                            id="enquiries-sort"
                            className="enquiries__select"
                            value={sort}
                            onChange={(ev) => selectSort(ev.target.value)}
                        >
                            {SORT_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <label className="enquiries__filter-label" htmlFor="enquiries-date-from">
                            Raised between
                        </label>
                        <input
                            type="date"
                            id="enquiries-date-from"
                            className="enquiries__date-input"
                            value={dateFrom}
                            onChange={(ev) => changeDateFrom(ev.target.value)}
                        />
                        <label className="enquiries__filter-label" htmlFor="enquiries-date-to">
                            and
                        </label>
                        <input
                            type="date"
                            id="enquiries-date-to"
                            className="enquiries__date-input"
                            value={dateTo}
                            onChange={(ev) => changeDateTo(ev.target.value)}
                        />
                        {dateFilterActive && (
                            <span
                                id="enquiries-dates-clear"
                                onClick={clearDates}
                                className="text-everflow-magenta underline cursor-pointer"
                            >
                                Clear dates
                            </span>
                        )}
                    </div>
                )}

                {loading && (
                    <div className="enquiries__scroll" id="enquiries-loading">
                        <div className="enquiries__table">
                            <div className="enquiries__header">
                                {['w-2/3', 'w-2/3', 'w-1/2', 'w-1/2', 'w-2/3'].map((width, index) => (
                                    <div className="enquiries__col" key={`skeleton-header-${index}`}>
                                        <SkeletonText className={width} />
                                    </div>
                                ))}
                            </div>
                            {Array.from({ length: 5 }).map((_, rowIndex) => (
                                <div className="enquiries__row" key={`skeleton-row-${rowIndex}`}>
                                    {['w-1/2', 'w-2/3', 'w-1/2', 'w-1/2', 'w-1/3'].map((width, cellIndex) => (
                                        <div className="enquiries__cell" key={`skeleton-cell-${rowIndex}-${cellIndex}`}>
                                            <SkeletonText className={width} />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {!loading && loadFailed && (
                    <div className="enquiries__empty" id="enquiries-error">
                        There was an error loading your enquiries, please click{' '}
                        <span
                            onClick={reload}
                            className="text-everflow-magenta underline cursor-pointer"
                        >
                            here
                        </span>{' '}
                        to try again.
                    </div>
                )}

                {!loading && !loadFailed && enquiries.length === 0 && filtersActive && (
                    <div className="enquiries__empty" id="enquiries-empty-filtered">
                        No enquiries match your search.
                    </div>
                )}

                {!loading && !loadFailed && enquiries.length === 0 && !filtersActive && (
                    <div className="enquiries__empty" id="enquiries-empty">
                        You have no enquiries
                    </div>
                )}

                {!loading && !loadFailed && enquiries.length > 0 && (
                    <>
                        <div className="enquiries__scroll">
                            <div className="enquiries__table" id="enquiries-table">
                                <div className="enquiries__header">
                                    <div className="enquiries__col">Enquiry number</div>
                                    <div className="enquiries__col">Query type</div>
                                    <div className="enquiries__col">Utility type</div>
                                    <div className="enquiries__col">Date raised</div>
                                    <div className="enquiries__col">Status</div>
                                </div>
                                {enquiries.map((enquiry, index) => {
                                    const cells = (
                                        <>
                                            <div className="enquiries__cell enquiries__cell--ref" id={`Enquiry${index}Reference`}>
                                                {enquiry.enquiryReference || '-'}
                                            </div>
                                            <div className="enquiries__cell" id={`Enquiry${index}QueryType`}>
                                                {getColumnValue(enquiry.queryType)}
                                            </div>
                                            <div className="enquiries__cell" id={`Enquiry${index}UtilityType`}>
                                                {getColumnValue(enquiry.utilityType)}
                                            </div>
                                            <div className="enquiries__cell" id={`Enquiry${index}Raised`}>
                                                {formatDate(enquiry.createdOn)}
                                            </div>
                                            <div className="enquiries__cell">
                                                <EnquiryStatusPill id={`Enquiry${index}Status`} status={enquiry.status} />
                                            </div>
                                        </>
                                    );

                                    return enquiry.enquiryReference ? (
                                        <Link
                                            to={`/enquiries/${enquiry.enquiryReference}`}
                                            className="enquiries__row enquiries__row--link"
                                            id={`Enquiry${index}Row`}
                                            key={`enquiry-${enquiry.enquiryReference}`}
                                        >
                                            {cells}
                                        </Link>
                                    ) : (
                                        <div className="enquiries__row" key={`enquiry-${index}`}>
                                            {cells}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="text-center mt-40px mb-40px">
                            <Pagination pages={maxPage} selected={page} callback={setPage} />
                        </div>
                    </>
                )}
            </div>
        </DashboardWrapper>
    );
}
